<script setup>
import { computed, toRef } from 'vue';
import { Card, ContentBanner, Skeleton } from '@dashhudson/dashing-ui';
import { useInteractionDetail } from '@/app/community/composables/useInteractionDetail';
import ConversationDetail from './ConversationDetail.vue';
import CommentDetail from './CommentDetail.vue';

const props = defineProps({
  loading: { type: Boolean, default: undefined },
  detailData: { type: Object, default: undefined },
  isEmbed: { type: Boolean, default: false },
});

const emit = defineEmits(['refresh']);

const interaction = toRef(props, 'detailData');
const { conversationType, commentType } = useInteractionDetail(interaction);

const noDataError = computed(() => !props.loading && !props.detailData);

function onRefresh() {
  emit('refresh');
}
</script>

<template>
  <div
    :class="['dash-w-full', 'dash-relative', '!h-screen', { 'interaction-detail-panel': !isEmbed }]"
  >
    <Card
      :class="[
        'dash-absolute',
        'dash-min-h-0',
        '!w-[calc(100%+2.5rem)]',
        '!rounded-tr-none',
        '!p-6',
        '!pb-10',
        '!pr-10',
        { '!h-[calc(100%+4.2rem)]': isEmbed, '!h-[calc(100%-1.8rem)]': !isEmbed },
      ]"
      active
    >
      <div v-if="loading" class="loading-skeleton dash-flex dash-flex-col dash-gap-6">
        <Skeleton width="100%" height="6rem" border-radius="30px" />
        <Skeleton width="100%" height="20rem" border-radius="30px" />
      </div>
      <div v-else-if="noDataError" class="dash-h-full dash-flex dash-items-center">
        <ContentBanner
          title="Couldn't Find a Message"
          message="Message failed to load. Please try again later."
          variant="sectionError"
          class="dash-m-auto"
        />
      </div>
      <template v-else>
        <ConversationDetail
          v-if="conversationType"
          :interaction-detail="interaction"
          @refresh="onRefresh"
        />
        <CommentDetail v-else-if="commentType" />
      </template>
    </Card>
  </div>
</template>

<style scoped>
.interaction-detail-panel {
  height: calc(
    100dvh - var(--hubspot-banner-height, 0px) - var(--importing-messages-banner-height, 0px)
  ) !important;
}
</style>
